import React, { useMemo } from 'react'
import { toDate } from '@ztechapps/common.utils'
import styles from './gantt.module.scss'
import type { GanttTask, TimelineComputed } from './types'
import { useFlattenTasks } from './TaskList'
import { TaskBar } from './TaskBar'

export type TimelineProps = {
  tasks: GanttTask[]
  tz: string
  rowHeight: number
  barHeight: number
  collapsedIds?: string[]
  timeline: TimelineComputed
  onTaskClick?: (task: GanttTask) => void
  headerRef?: React.RefObject<HTMLDivElement>
  bodyRef?: React.RefObject<HTMLDivElement>
}

export const Timeline = ({
  tasks,
  tz,
  rowHeight,
  barHeight,
  collapsedIds,
  timeline,
  onTaskClick,
  headerRef,
  bodyRef,
}: TimelineProps) => {
  const rows = useFlattenTasks(tasks, collapsedIds).filter((r) => r.isVisible)
  const height = rows.length * rowHeight

  const todayX = useMemo(() => {
    const todayIso = toDate(undefined, tz).toISOString()
    return timeline.toX(todayIso)
  }, [timeline, tz])

  // group days by ISO week (Mon-Sun)
  const weeks = useMemo(() => {
    type W = { key: string; startIso: string; dayCount: number }
    const out: W[] = []
    let current: W | null = null
    timeline.days.forEach((d) => {
      const m = toDate(d.iso, tz)
      const key = `${m.isoWeekYear()}-${m.isoWeek()}`
      if (!current || current.key !== key) {
        if (current) out.push(current)
        current = {
          key,
          startIso: m.clone().startOf('isoWeek').toISOString(),
          dayCount: 1,
        }
      } else {
        current.dayCount += 1
      }
    })
    if (current) out.push(current)
    return out
  }, [timeline.days, tz])

  return (
    <div className={styles.rightPane}>
      <div className={styles.timelineHeader} ref={headerRef}>
        <div className={styles.timelineHeaderInner} style={{ width: timeline.gridWidth }}>
          <div className={styles.weekRow}>
            {weeks.map((w) => (
              <div key={w.key} className={styles.weekCell} style={{ width: w.dayCount * timeline.columnWidth }}>
                {toDate(w.startIso, tz).format('Do MMM YYYY')}
              </div>
            ))}
          </div>
          <div className={styles.dayRow} style={{ ['--col-width' as any]: `${timeline.columnWidth}px` }}>
            {timeline.days.map((d) => (
              <div key={d.iso} className={[styles.dayCell, d.isWeekend ? styles.dayWeekend : ''].join(' ')}>
                {toDate(d.iso, tz).format('dd').charAt(0)}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.timelineBody} ref={bodyRef}>
        <svg className={styles.gridSvg} width={timeline.gridWidth} height={height}>
          {timeline.days.map((d, i) =>
            d.isWeekend ? (
              <rect
                key={`w-${i}`}
                className={styles.weekendRect}
                x={i * timeline.columnWidth}
                y={0}
                width={timeline.columnWidth}
                height={height}
              />
            ) : null
          )}
          {timeline.days.map((d, i) => (
            <line
              key={`g-${i}`}
              className={styles.gridLine}
              x1={i * timeline.columnWidth}
              y1={0}
              x2={i * timeline.columnWidth}
              y2={height}
            />
          ))}
          {todayX >= 0 && todayX <= timeline.gridWidth && (
            <line className={styles.todayLine} x1={todayX} y1={0} x2={todayX} y2={height} />
          )}

          {rows.map((r, idx) => (
            <TaskBar
              key={r.task.id}
              task={r.task}
              rowIndex={idx}
              rowHeight={rowHeight}
              barHeight={barHeight}
              tz={tz}
              timeline={timeline}
              onClick={onTaskClick}
            />
          ))}
        </svg>
      </div>
    </div>
  )
}
